import type { Database } from "@habity/types";

// User events that are stored in the database (user_actions table)
// These must match the user_event enum in the database
export const USER_EVENTS = {
  // Seller events
  CLAIMED_HOME: "claimed_home",
  BOOKED_MEETING: "booked_meeting",
  BOOKED_ONLINE_MEETING: "booked_online_meeting",
  REQUESTED_BROKER_VALUATION: "requested_broker_valuation",
  REQUESTED_BROKER_VALUATION_AFTER_HOME_INTERACTION:
    "requested_broker_valuation_after_home_interaction",
  REQUESTED_BROKER_VALUATION_AFTER_AVM_ESTIMATE:
    "requested_broker_valuation_after_avm_estimate",
  PERFORMED_AVM_ESTIMATE: "performed_avm_estimate",
  VIEWED_SPECULATORS_OF_HOME: "viewed_speculators_of_home",
  MATCHED_HOME: "matched_home",
  BOOKED_BROKER_VALUATION: "booked_broker_valuation",
  REQUESTED_MEETING: "requested_meeting",
  // Buyer events
  MATCHED: "matched",
  LIKED: "liked",
  SHOWED_INTEREST: "showed_interest",
  SHOWED_STRONG_INTEREST: "showed_strong_interest",
  BOOKED_VIEWING: "booked_viewing",
  ATTENDED_VIEWING: "attended_viewing",
  FOLLOWED_BIDDING: "followed_bidding",
  PAUSED_BIDDING: "paused_bidding",
  PLACED_BID: "placed_bid",
  BOOKED_CONTRACT_SIGNING: "booked_contract_signing",
  RESERVED_HOME: "reserved_home",
  DECLINED_INTEREST: "declined_interest",
  ARCHIVED: "archived",
  ADDED_TO_WISHLIST: "added_to_wishlist",
  CLICKED_PUBLIC_PREVIEW_HOME: "clicked_public_preview_home",
  // Other user events
  CREATED_SEARCH_PROFILE: "created_search_profile",
  // Lead form events
  ENTERED_SELLER_LEAD_FORM: "entered_seller_lead_form",
  ENTERED_BUYER_LEAD_FORM: "entered_buyer_lead_form",
  // Ad events
  CLICKED_IMAGE_AD: "clicked_image_ad",
  // Final price subscription
  SUBSCRIBED_FINAL_PRICE: "subscribed_final_price",
} as const satisfies Record<string, Database["public"]["Enums"]["user_event"]>;

// Tracking-only events (not stored in database, only for analytics)
export const TRACKING_EVENTS = {
  // Authentication events
  SIGNED_IN: "signed_in",
  REQUESTED_MAGIC_LINK: "requested_magic_link",
  CREATED_USER: "created_user",
  IDENTIFIED_USER: "identified_user",
  COMPLETED_USER_PROFILE: "completed_user_profile",

  // Search and navigation events
  SEARCHED_LOCATION: "searched_location",
  SEARCHED_ADDRESS: "searched_address",
  SEARCHED_FOCUS_AREA: "searched_focus_area",

  // Search profile events (tracking only, not user actions)
  REMOVED_SEARCH_PROFILE: "removed_search_profile",
  SEARCH_PROFILE_UPDATED: "search_profile_updated",

  // Removal/negative events (tracking only)
  REMOVED_INTEREST: "removed_interest",
  REMOVED_STRONG_INTEREST: "removed_strong_interest",
  UNFOLLOWED_BIDDING: "unfollowed_bidding",
  RESUMED_BIDDING: "resumed_bidding",
  REMOVED_BID: "removed_bid",
  UNBOOKED_VIEWING: "unbooked_viewing",
  UNATTENDED_VIEWING: "unattended_viewing",
  UNLIKED: "unliked",
  REMOVED_FROM_WISHLIST: "removed_from_wishlist",
  UNBOOKED_CONTRACT_SIGNING: "unbooked_contract_signing",

  // Project events (tracking only)
  PROJECT_INTEREST_ADDED: "project_interest_added",
  PROJECT_INTEREST_REMOVED: "project_interest_removed",

  // Identity events
  INITIATED_BANKID_AUTH: "initiated_bankid_auth",
  VERIFIED_IDENTITY: "verified_identity",

  // Seller dashboard events
  UPLOADED_HOME_IMAGE: "uploaded_home_image",
  REMOVED_HOME_IMAGE: "removed_home_image",
  PUBLISHED_HOME: "published_home",
  UNPUBLISHED_HOME: "unpublished_home",
  EDITED_HOME: "edited_home",

  // Sell stepper events
  SELLER_ONBOARDING_SELECTED_ADDRESS: "seller_onboarding_selected_address",
  SELLER_ONBOARDING_COMPLETED_STEP: "seller_onboarding_completed_step",
  SELLER_ONBOARDING_STARTED: "seller_onboarding_started",
  SELLER_ONBOARDING_COMPLETED: "seller_onboarding_completed",
  SELLER_ONBOARDING_SELECTED_INTENT: "seller_onboarding_selected_intent",

  // Buy stepper events
  BUYER_ONBOARDING_SELECTED_LOCATION: "buyer_onboarding_selected_location",
  BUYER_ONBOARDING_COMPLETED_STEP: "buyer_onboarding_completed_step",
  BUYER_ONBOARDING_STARTED: "buyer_onboarding_started",
  BUYER_ONBOARDING_COMPLETED: "buyer_onboarding_completed",

  // Loan contact events
  LOAN_CONTACT_LEAD_CREATED: "loan_contact_lead_created",

  // Home lifecycle events
  HOME_PUBLISHED: "home_published",
} as const;

// Combined EVENTS object for backward compatibility
export const EVENTS = {
  ...USER_EVENTS,
  ...TRACKING_EVENTS,
} as const;

// Type to ensure every event has a description
type EventValues = (typeof EVENTS)[keyof typeof EVENTS];

// Event descriptions for admin dashboard and documentation
// This type ensures every event in EVENTS must have a corresponding description
export const EVENT_DESCRIPTIONS: Record<EventValues, string> = {
  // Authentication events
  [EVENTS.SIGNED_IN]: "User successfully signed in",
  [EVENTS.REQUESTED_MAGIC_LINK]: "User requested magic link for authentication",
  [EVENTS.CREATED_USER]: "New user account was created",
  [EVENTS.IDENTIFIED_USER]: "User was identified in tracking system",
  [EVENTS.COMPLETED_USER_PROFILE]: "User completed their profile information",

  // Search and navigation events
  [EVENTS.SEARCHED_LOCATION]: "User searched for a location",
  [EVENTS.SEARCHED_ADDRESS]: "User searched for an address",
  [EVENTS.SEARCHED_FOCUS_AREA]: "User searched for a focus area",
  [EVENTS.CREATED_SEARCH_PROFILE]: "User created a search profile",
  [EVENTS.SEARCH_PROFILE_UPDATED]: "User updated an existing search profile",
  [EVENTS.REMOVED_SEARCH_PROFILE]: "User removed a search profile",

  // User engagement events
  [EVENTS.SHOWED_INTEREST]: "User expressed interest in a property",
  [EVENTS.REMOVED_INTEREST]: "User removed interest in a property",
  [EVENTS.SHOWED_STRONG_INTEREST]:
    "User expressed strong interest in a property",
  [EVENTS.REMOVED_STRONG_INTEREST]:
    "User removed strong interest in a property",
  [EVENTS.BOOKED_VIEWING]: "User booked a property viewing",
  [EVENTS.UNBOOKED_VIEWING]: "User unbooked a property viewing",
  [EVENTS.ATTENDED_VIEWING]: "User attended a property viewing",
  [EVENTS.UNATTENDED_VIEWING]: "User unattended a property viewing",
  [EVENTS.MATCHED]: "User matched with a property",
  [EVENTS.LIKED]: "User liked a property",
  [EVENTS.UNLIKED]: "User unliked a property",
  [EVENTS.FOLLOWED_BIDDING]: "User followed a bidding",
  [EVENTS.UNFOLLOWED_BIDDING]: "User unfollowed a bidding",
  [EVENTS.PAUSED_BIDDING]: "User paused a bidding",
  [EVENTS.RESUMED_BIDDING]: "User resumed a bidding",
  [EVENTS.PLACED_BID]: "User placed a bid",
  [EVENTS.REMOVED_BID]: "User removed a bid",
  [EVENTS.DECLINED_INTEREST]: "User declined interest in a property",
  [EVENTS.BOOKED_CONTRACT_SIGNING]: "User booked a contract signing",
  [EVENTS.UNBOOKED_CONTRACT_SIGNING]: "User unbooked a contract signing",
  [EVENTS.RESERVED_HOME]: "User reserved a home",
  [EVENTS.ARCHIVED]: "User archived an item",
  [EVENTS.ADDED_TO_WISHLIST]: "User added a property to wishlist",
  [EVENTS.CLICKED_PUBLIC_PREVIEW_HOME]:
    "User clicked on a home with public preview enabled (e.g., premarket)",
  [EVENTS.REMOVED_FROM_WISHLIST]: "User removed a property from wishlist",

  // Project events
  [EVENTS.PROJECT_INTEREST_ADDED]: "User showed interest in a project",
  [EVENTS.PROJECT_INTEREST_REMOVED]: "User removed interest from a project",

  // Identity events
  [EVENTS.INITIATED_BANKID_AUTH]: "User initiated BankID authentication",
  [EVENTS.VERIFIED_IDENTITY]: "User successfully verified their identity",

  // Seller dashboard events
  [EVENTS.UPLOADED_HOME_IMAGE]: "User uploaded an image in seller dashboard",
  [EVENTS.REMOVED_HOME_IMAGE]: "User removed an image in seller dashboard",
  [EVENTS.PUBLISHED_HOME]: "User published their home in seller dashboard",
  [EVENTS.UNPUBLISHED_HOME]: "User unpublished their home in seller dashboard",
  [EVENTS.EDITED_HOME]: "User edited property information",

  // Sell intent-specific events
  [EVENTS.CLAIMED_HOME]: "User claimed their property to test the market",
  [EVENTS.BOOKED_MEETING]: "User booked a meeting with a broker via Cal.com",
  [EVENTS.BOOKED_ONLINE_MEETING]: "User scheduled an online meeting with a broker",
  [EVENTS.REQUESTED_BROKER_VALUATION]: "User requested a property valuation via seller onboarding (no Cal booking)",
  [EVENTS.REQUESTED_BROKER_VALUATION_AFTER_HOME_INTERACTION]:
    "User requested broker valuation after interacting with a home",
  [EVENTS.REQUESTED_BROKER_VALUATION_AFTER_AVM_ESTIMATE]:
    "User requested broker valuation after viewing AVM estimate",
  [EVENTS.PERFORMED_AVM_ESTIMATE]:
    "User performed an AVM estimate on their property",
  [EVENTS.VIEWED_SPECULATORS_OF_HOME]:
    "User viewed speculators of their home in sell flow",
  [EVENTS.MATCHED_HOME]:
    "System matched a home with speculators via daily matching task",
  [EVENTS.BOOKED_BROKER_VALUATION]:
    "User booked a broker valuation via Cal.com (confirmed booking)",
  [EVENTS.REQUESTED_MEETING]:
    "User requested a meeting with a broker via seller onboarding (no Cal booking)",

  // Selling process events
  [EVENTS.SELLER_ONBOARDING_SELECTED_ADDRESS]:
    "User selected an address in sell flow",
  [EVENTS.SELLER_ONBOARDING_COMPLETED_STEP]:
    "User completed a step in the selling process",
  [EVENTS.SELLER_ONBOARDING_STARTED]: "User started the selling process",
  [EVENTS.SELLER_ONBOARDING_COMPLETED]:
    "User completed the entire selling process",
  [EVENTS.SELLER_ONBOARDING_SELECTED_INTENT]:
    "User selected their selling intent (test market, sell now, or valuation)",

  // Buying process events
  [EVENTS.BUYER_ONBOARDING_SELECTED_LOCATION]:
    "User selected a location in buy flow",
  [EVENTS.BUYER_ONBOARDING_COMPLETED_STEP]:
    "User completed a step in the buying process",
  [EVENTS.BUYER_ONBOARDING_STARTED]: "User started the buying process",
  [EVENTS.BUYER_ONBOARDING_COMPLETED]:
    "User completed the entire buying process",

  // Loan contact events
  [EVENTS.LOAN_CONTACT_LEAD_CREATED]:
    "Söderberg lead was successfully created for user loan contact request",

  // Home lifecycle events
  [EVENTS.HOME_PUBLISHED]:
    "Home was published to the platform with published_at date from Vitec",

  // Lead form events
  [EVENTS.ENTERED_SELLER_LEAD_FORM]:
    "User submitted a seller lead capture form",
  [EVENTS.ENTERED_BUYER_LEAD_FORM]: "User submitted a buyer lead capture form",

  // Ad events
  [EVENTS.CLICKED_IMAGE_AD]: "User clicked on an image advertisement",

  // Final price subscription
  [EVENTS.SUBSCRIBED_FINAL_PRICE]:
    "User subscribed to receive final price notification for a property",
};

export function getHomePublishedSyncVersion(): number {
  return 10;
}
